import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import {
    EmptyValue,
    RecordCard,
    RecordTableToolbar,
    recordTablePagination,
    ValueChip,
} from '@client/components/_ui/display/RecordPrimitives';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { createColumnHelper, PrismaTable, TableEmptyState, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Input, Select, Tooltip } from 'antd';
import { useState } from 'react';
import { VehicleDetailsDrawer } from '../VehicleDetailsDrawer';
import { isConsolidatorRecord } from './ClientRecordHeader';
import { useCanManageFleetClients, useClientRecord } from './client-record-data';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database/details/$id/vehicles')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <VehiclesTab />
        </PageController>
    );
}

type VehicleQueryParams = RouterInputs['fleetClientDb']['listFleetVehicles'];
type VehicleWhere = NonNullable<VehicleQueryParams['where']>;
type VehicleRow = NonNullable<RouterOutputs['fleetClientDb']['listFleetVehicles']>['rows'][number];
const columnHelper = createColumnHelper<VehicleRow, NonNullable<VehicleQueryParams['orderBy']>>();

function searchWhere(value: string): Pick<VehicleWhere, 'OR'> {
    const term = value.trim();
    if (!term) {
        return { OR: undefined };
    }
    return {
        OR: [
            { registration: { contains: term, mode: 'insensitive' } },
            { make: { contains: term, mode: 'insensitive' } },
            { description: { contains: term, mode: 'insensitive' } },
            { PrimaryDriver: { is: { name: { contains: term, mode: 'insensitive' } } } },
        ],
    };
}

function VehiclesTab() {
    const { id } = Route.useParams();
    const { data: client } = useClientRecord(id);
    const canVerify = useCanManageFleetClients();
    const { formatDate } = useFormatter();
    const [params, setParams] = useState<VehicleQueryParams>({
        limit: 20,
        orderBy: { registration: 'asc' },
        where: {},
    });
    const [searchText, setSearchText] = useState('');
    const [selectedVehicleId, setSelectedVehicleId] = useState<bigint | null>(null);

    const isClient = client ? !isConsolidatorRecord(client) : false;
    const vehiclesQuery = trpc.fleetClientDb.listFleetVehicles.useQuery(
        isClient ? { ...params, where: { ...params.where, clientId: id } } : skipToken,
    );
    const optionsQuery = trpc.fleetClientDb.listFleetVehicleFilterOptions.useQuery(
        isClient ? { clientId: id } : skipToken,
        { meta: { errorSurface: 'inline' } },
    );

    const setWhere = (patch: Partial<VehicleWhere>) =>
        setParams((prev) => ({ ...prev, offset: 0, where: { ...prev.where, ...patch } }));
    const leaseTypeFilter = typeof params.where?.leaseType === 'string' ? params.where.leaseType : undefined;
    const insuranceFilter = typeof params.where?.insurance === 'string' ? params.where.insurance : undefined;
    const hasActiveFilters = !!params.where?.OR || !!leaseTypeFilter || !!insuranceFilter;
    const filterOptionsUnavailable = !!optionsQuery.error && !optionsQuery.data;

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: vehiclesQuery.data?.rows || [],
        total: vehiclesQuery.data?.total || 0,
        loading: vehiclesQuery.isLoading,
        queryParams: params,
        onParamsChange: setParams,
        pagination: recordTablePagination,
        emptyContent: (
            <TableEmptyState
                hasActiveFilters={hasActiveFilters}
                noRecordsTitle="No vehicles"
                noRecordsDescription="Vehicles arrive with the carparc import."
                noMatchesTitle="No vehicles match"
                noMatchesDescription="Try another rego, model or driver, or clear the lease and insurance filters."
            />
        ),
        columns: [
            columnHelper.accessor('registration', {
                id: 'registration',
                header: 'Rego',
                sortable: 'registration',
                width: 130,
                cell: ({ row }) => (
                    <span className="flex items-baseline gap-2 whitespace-nowrap">
                        <span className="font-semibold tabular-nums">{row.registration}</span>
                        {row.stateOfRegistration ? (
                            <span className="text-subtle-foreground text-xs">{row.stateOfRegistration}</span>
                        ) : null}
                    </span>
                ),
            }),
            columnHelper.accessor('make', {
                id: 'vehicle',
                header: 'Vehicle',
                sortable: 'make',
                ellipsis: true,
                cell: ({ row }) => {
                    const model = [row.make, row.description].filter(Boolean).join(' ');
                    return (
                        <span className="flex min-w-0 items-baseline gap-2">
                            {model ? (
                                <span className="truncate font-medium">{model}</span>
                            ) : (
                                <EmptyValue>Make not recorded</EmptyValue>
                            )}
                            {row.colour ? (
                                <span className="shrink-0 text-subtle-foreground text-xs">{row.colour}</span>
                            ) : null}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('leaseType', {
                id: 'leaseType',
                header: 'Lease type',
                sortable: 'leaseType',
                width: 150,
                cell: ({ row }) => row.leaseType || <EmptyValue />,
            }),
            columnHelper.accessor('leaseEndDate', {
                id: 'leaseEnd',
                header: 'Lease ends',
                sortable: 'leaseEndDate',
                width: 150,
                cell: ({ row }) => {
                    if (!row.leaseEndDate) {
                        return <EmptyValue />;
                    }
                    const ended = row.leaseEndDate.getTime() < Date.now();
                    return ended ? (
                        <Tooltip title="Lease end date has passed">
                            <span className="whitespace-nowrap text-warning-foreground tabular-nums">
                                {formatDate(row.leaseEndDate)}
                                <span className="ml-1.5 font-medium text-xs">Ended</span>
                            </span>
                        </Tooltip>
                    ) : (
                        <span className="whitespace-nowrap tabular-nums">{formatDate(row.leaseEndDate)}</span>
                    );
                },
            }),
            columnHelper.accessor('insurance', {
                id: 'insurance',
                header: 'Insurance',
                sortable: 'insurance',
                width: 120,
                cell: ({ row }) => (row.insurance ? <ValueChip>{row.insurance}</ValueChip> : <EmptyValue />),
            }),
            columnHelper.accessor((row) => row.PrimaryDriver?.name, {
                id: 'driver',
                header: 'Driver',
                width: 160,
                ellipsis: true,
                cell: ({ row }) => row.PrimaryDriver?.name || <EmptyValue>No driver</EmptyValue>,
            }),
            columnHelper.accessor((row) => row.FleetCostCentre?.name, {
                id: 'costCentre',
                header: 'Cost centre',
                width: 130,
                ellipsis: true,
                cell: ({ row }) => row.FleetCostCentre?.name || <EmptyValue />,
            }),
            columnHelper.actions({
                width: 44,
                cell: ({ row }) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <Button.View tooltip="View vehicle" onClick={() => setSelectedVehicleId(row.id)} />
                    </div>
                ),
            }),
        ],
    });

    if (client && !isClient) {
        return null;
    }

    return (
        <>
            <RecordCard padded={false}>
                <RecordTableToolbar>
                    <Input.Search
                        placeholder="Search rego, model or driver"
                        allowClear
                        className="w-72"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        onSearch={(value) => setWhere(searchWhere(value))}
                    />
                    <Select
                        placeholder="Lease type"
                        allowClear
                        className="w-44"
                        value={leaseTypeFilter}
                        loading={optionsQuery.isFetching}
                        disabled={filterOptionsUnavailable}
                        options={(optionsQuery.data?.leaseTypes ?? []).map((value) => ({ value, label: value }))}
                        onChange={(value: string | undefined) => setWhere({ leaseType: value })}
                    />
                    <Select
                        placeholder="Insurance"
                        allowClear
                        className="w-40"
                        value={insuranceFilter}
                        loading={optionsQuery.isFetching}
                        disabled={filterOptionsUnavailable}
                        options={(optionsQuery.data?.insurances ?? []).map((value) => ({ value, label: value }))}
                        onChange={(value: string | undefined) => setWhere({ insurance: value })}
                    />
                </RecordTableToolbar>
                {optionsQuery.error ? (
                    <div className="border-separator border-b px-4 py-3">
                        <InlineQueryError
                            compact
                            title="Could not load vehicle filter options"
                            description="Lease type and insurance options are unavailable. You can still search and view vehicles."
                            error={optionsQuery.error}
                            onRetry={() => void optionsQuery.refetch()}
                            retrying={optionsQuery.isFetching}
                        />
                    </div>
                ) : null}
                {vehiclesQuery.error ? (
                    <div className="p-4">
                        <InlineQueryError
                            compact
                            error={vehiclesQuery.error}
                            onRetry={() => void vehiclesQuery.refetch()}
                        />
                    </div>
                ) : (
                    <PrismaTable
                        {...tableProps}
                        // Preserve column widths on smaller cards and let the Vehicle column absorb extra space.
                        tableLayout="fixed"
                        scroll={tableProps.dataSource?.length ? { x: 1100 } : undefined}
                        onRow={({ id: vehicleId }) => ({
                            className: 'cursor-pointer',
                            onClick() {
                                setSelectedVehicleId(vehicleId);
                            },
                        })}
                    />
                )}
            </RecordCard>

            <VehicleDetailsDrawer
                vehicleId={selectedVehicleId}
                canVerify={canVerify}
                onClose={() => setSelectedVehicleId(null)}
            />
        </>
    );
}
